import { otherColors } from '@ehrTheme/colors';
import AssignmentIndOutlinedIcon from '@mui/icons-material/AssignmentIndOutlined';
import CancelIcon from '@mui/icons-material/Cancel';
import DownloadIcon from '@mui/icons-material/Download';
import FolderOutlinedIcon from '@mui/icons-material/FolderOutlined';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import { LoadingButton } from '@mui/lab';
import {
  Box,
  Button,
  Checkbox,
  CircularProgress,
  FormControl,
  Grid,
  IconButton,
  Link as MUILink,
  MenuItem,
  Paper,
  Select,
  Skeleton,
  Stack,
  TextField,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import Alert, { AlertColor } from '@mui/material/Alert';
import Snackbar from '@mui/material/Snackbar';
import { useMutation, UseMutationResult, useQuery, useQueryClient } from '@tanstack/react-query';
import { Appointment, Attachment, Flag, Organization } from 'fhir/r4b';
import { DateTime } from 'luxon';
import { enqueueSnackbar } from 'notistack';
import React, { ReactElement, useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  createZ3Object,
  deleteVisitFiles,
  generatePaperworkPdf,
  getOrCreateVisitDetailsPdf,
  getPatientVisitDetails,
  getPatientVisitFiles,
  getVisitFaxHistory,
  updatePatientVisitDetails,
  updateVisitFiles,
} from 'src/api/api';
import ImageCarousel, { ImageCarouselObject } from 'src/components/ImageCarousel';
import ImageUploader from 'src/components/ImageUploader';
import PatientBalances from 'src/components/PatientBalances';
import { RoundedButton } from 'src/components/RoundedButton';
import { ScannerModal } from 'src/components/ScannerModal';
import { useOystehrAPIClient } from 'src/features/visits/shared/hooks/useOystehrAPIClient';
import { useGetPatientAccount, useGetPatientCoverages } from 'src/hooks/useGetPatient';
import { useGetPatientBalances } from 'src/hooks/useGetPatientBalances';
import { useGetPatientDocs } from 'src/hooks/useGetPatientDocs';
import { useGetPatientPaymentsList } from 'src/hooks/useGetPatientPaymentsList';
import {
  BOOKING_CONFIG,
  DocumentInfo,
  DocumentType,
  EHRVisitDetails,
  FHIR_EXTENSION,
  FhirAppointmentType,
  formatDateForDisplay,
  getCancellationReasonDisplay,
  getCoding,
  getFullName,
  getInPersonVisitStatus,
  getPatchOperationForNewMetaTag,
  getReasonForVisitAndAdditionalDetailsFromAppointment,
  getReasonForVisitOptionsForServiceCategory,
  getUnconfirmedDOBForAppointment,
  GetVisitFaxHistoryOutput,
  isApiError,
  isInPersonAppointment,
  isTelemedAppointment,
  makeAbbreviation,
  OrderedCoveragesWithSubscribers,
  PATIENT_INFO_META_DATA_RETURNING_PATIENT_CODE,
  PATIENT_INFO_META_DATA_SYSTEM,
  PatientAccountResponse,
  SERVICE_CATEGORY_SYSTEM,
  ServiceCategoryCode,
  ServiceMode,
  UpdateVisitDetailsInput,
  UpdateVisitFilesInput,
  VisitDocuments,
  VisitStatusLabel,
} from 'utils';
import AppointmentNotesHistory from '../components/AppointmentNotesHistory';
import CardGridItem from '../components/CardGridItem';
import CustomBreadcrumbs from '../components/CustomBreadcrumbs';
import DateSearch from '../components/DateSearch';
import {
  ActivityLogDialog,
  CancellationReasonDialog,
  CustomDialog,
  EditPatientInfoDialog,
  ReportIssueDialog,
} from '../components/dialogs';
import { InPersonAppointmentStatusChip } from '../components/InPersonAppointmentStatusChip';
import PaperworkFlagIndicator from '../components/PaperworkFlagIndicator';
import PatientInformation, { IconProps } from '../components/PatientInformation';
import PatientPaymentList from '../components/PatientPaymentsList';
import { PriorityIconWithBorder } from '../components/PriorityIconWithBorder';
import { HOP_QUEUE_URI } from '../constants';
import { dataTestIds } from '../constants/data-test-ids';
import { FEATURE_FLAGS } from '../constants/feature-flags';
import { ChangeStatusDropdown } from '../features/visits/in-person/components/ChangeStatusDropdown';
import { PencilIconButton } from '../features/visits/telemed/components/patient-visit-details/PencilIconButton';
import { formatLastModifiedTag } from '../helpers';
import {
  ActivityLogData,
  ActivityName,
  formatActivityLogs,
  formatNotesHistory,
  getAppointmentAndPatientHistory,
  getCriticalUpdateTagOp,
  NoteHistory,
} from '../helpers/activityLogsUtils';
import { useApiClients } from '../hooks/useAppClients';
import useEvolveUser from '../hooks/useEvolveUser';
import PageContainer from '../layout/PageContainer';
import { PatientAccountComponent } from './PatientInformationPage';

const consentToTreatPatientDetailsKey = 'Consent Forms signed?';

interface EditDOBParams {
  dob?: DateTime | null;
}

interface EditReasonForVisitParams {
  reasonForVisit?: string;
  additionalDetails?: string;
}

interface EditNLGParams {
  guardians?: string;
}

interface ServiceCategoryParams {
  serviceCategory?: ServiceCategoryCode;
}

type EditDialogConfig =
  | {
      type: 'closed';
      values: object;
      keyTitleMap: object;
    }
  | { type: 'dob'; values: EditDOBParams; keyTitleMap: { dob: 'DOB' }; requiredKeys: string[] }
  | {
      type: 'reason-for-visit';
      values: EditReasonForVisitParams;
      keyTitleMap: { reasonForVisit: 'Reason for Visit'; additionalDetails: 'Additional Details' };
      requiredKeys: string[];
    }
  | { type: 'nlg'; values: EditNLGParams; keyTitleMap: { guardians: 'Guardians' }; requiredKeys: string[] }
  | {
      type: 'service-category';
      values: ServiceCategoryParams;
      keyTitleMap: { serviceCategory: 'Service Category' };
      requiredKeys: string[];
    };

const dialogTitleFromType = (type: EditDialogConfig['type']): string => {
  switch (type) {
    case 'dob':
      return "Please enter patient's confirmed date of birth";
    case 'reason-for-visit':
      return "Please enter patient's reason for visit";
    case 'nlg':
      return "Please enter patient's Authorized Non-Legal Guardians";
    case 'service-category':
      return 'Please select Service Category';
    default:
      return '';
  }
};

const CLOSED_EDIT_DIALOG: EditDialogConfig = Object.freeze({
  type: 'closed',
  values: {},
  keyTitleMap: {},
});

interface SavedCardItem {
  front: DocumentInfo | null;
  frontId: string | null;
  back: DocumentInfo | null;
  backId: string | null;
}

type SavedCardCategory = 'id' | 'primary-ins' | 'secondary-ins';

const usePatientData = (
  patientId?: string
): {
  account?: PatientAccountResponse;
  insurance?: {
    coverages: OrderedCoveragesWithSubscribers;
    insuranceOrgs: Organization[];
  };
  isFetching: boolean;
} => {
  const apiClient = useOystehrAPIClient();

  const accountQuery = useGetPatientAccount({
    apiClient,
    patientId: patientId ?? null,
  });

  const coveragesQuery = useGetPatientCoverages({ apiClient, patientId: patientId ?? null }, undefined, {
    enabled: accountQuery.status === 'success',
  });

  return {
    account: accountQuery.data,
    insurance: coveragesQuery.data,
    isFetching: accountQuery.isFetching || coveragesQuery.isFetching,
  };
};

export default function VisitDetailsPage(): ReactElement {
  // variables
  const { id: appointmentID } = useParams();
  const { oystehr, oystehrZambda } = useApiClients();
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('lg'));
  const cardSectionHeight = isSmallScreen ? '120px' : '180px';

  const queryClient = useQueryClient();

  const navigate = useNavigate();

  // state variables
  const [status, setStatus] = useState<VisitStatusLabel | undefined>(undefined);
  const [errors, setErrors] = useState<{ hopError?: string }>({});
  const [toastMessage, setToastMessage] = React.useState<string | undefined>(undefined);
  const [toastType, setToastType] = React.useState<AlertColor | undefined>(undefined);
  const [snackbarOpen, setSnackbarOpen] = React.useState<boolean>(false);
  const [paperworkPdfLoading, setPaperworkPdfLoading] = React.useState<boolean>(false);
  const [visitDetailsPdfLoading, setVisitDetailsPdfLoading] = React.useState<boolean>(false);

  const [consentAttested, setConsentAttested] = useState<boolean | null>(null);

  const [editDialogConfig, setEditDialogConfig] = useState<EditDialogConfig>(CLOSED_EDIT_DIALOG);

  // File variables

  const [cancelDialogOpen, setCancelDialogOpen] = useState<boolean>(false);
  const [hopQueueDialogOpen, setHopQueueDialogOpen] = useState<boolean>(false);
  const [photoZoom, setPhotoZoom] = useState<boolean>(false);
  const [zoomedIdx, setZoomedIdx] = useState<number>(0);
  const [issueDialogOpen, setIssueDialogOpen] = useState<boolean>(false);
  const [activityLogDialogOpen, setActivityLogDialogOpen] = useState<boolean>(false);
  const [activityLogsLoading, setActivityLogsLoading] = useState<boolean>(true);
  const [activityLogs, setActivityLogs] = useState<ActivityLogData[] | undefined>(undefined);
  const [notesHistory, setNotesHistory] = useState<NoteHistory[] | undefined>(undefined);
  const [scannerModalOpen, setScannerModalOpen] = useState<boolean>(false);
  const [scannerFileType, setScannerFileType] = useState<UpdateVisitFilesInput['fileType'] | null>(null);
  const [uploadingFileType, setUploadingFileType] = useState<UpdateVisitFilesInput['fileType'] | null>(null);
  const [deletingFileId, setDeletingFile] = useState<string | null>(null);
  const user = useEvolveUser();

  const {
    data: imageFileData,
    isLoading: imagesLoading,
    refetch: refetchFileData,
  } = useQuery({
    queryKey: ['get-visit-files', appointmentID],

    queryFn: async (): Promise<VisitDocuments> => {
      if (oystehrZambda && appointmentID) {
        return getPatientVisitFiles(oystehrZambda, { appointmentId: appointmentID });
      }
      throw new Error('fhir client not defined or patientIds not provided');
    },

    enabled: Boolean(oystehrZambda) && appointmentID !== undefined,
  });

  const { fullCardPdfs, consentPdfUrls } = imageFileData || {
    fullCardPdfs: [],
    consentPdfUrls: [],
  };

  const { idCards, primaryInsuranceCards, secondaryInsuranceCards, imageCarouselObjs } = (() => {
    const { photoIdCards, insuranceCards, insuranceCardsSecondary } = imageFileData || {
      photoIdCards: [],
      insuranceCards: [],
      insuranceCardsSecondary: [],
    };
    const idCards: SavedCardItem = { front: null, frontId: null, back: null, backId: null };
    const imageCarouselObjs: ImageCarouselObject[] = [];
    const primaryInsuranceCards: SavedCardItem = {
      front: null,
      frontId: null,
      back: null,
      backId: null,
    };
    const secondaryInsuranceCards: SavedCardItem = {
      front: null,
      frontId: null,
      back: null,
      backId: null,
    };
    insuranceCards.forEach((card) => {
      imageCarouselObjs.push({ alt: card.type, url: card.presignedUrl || '' });
      if (card.type === DocumentType.InsuranceFront) {
        primaryInsuranceCards.front = card;
        primaryInsuranceCards.frontId = card.id;
      } else if (card.type === DocumentType.InsuranceBack) {
        primaryInsuranceCards.back = card;
        primaryInsuranceCards.backId = card.id;
      }
    });
    insuranceCardsSecondary.forEach((card) => {
      imageCarouselObjs.push({ alt: card.type, url: card.presignedUrl || '' });
      if (card.type === DocumentType.InsuranceFrontSecondary) {
        secondaryInsuranceCards.front = card;
        secondaryInsuranceCards.frontId = card.id;
      } else if (card.type === DocumentType.InsuranceBackSecondary) {
        secondaryInsuranceCards.back = card;
        secondaryInsuranceCards.backId = card.id;
      }
    });
    photoIdCards.forEach((card) => {
      imageCarouselObjs.push({ alt: card.type, url: card.presignedUrl || '' });
      if (card.type === DocumentType.PhotoIdFront) {
        idCards.front = card;
        idCards.frontId = card.id;
      } else if (card.type === DocumentType.PhotoIdBack) {
        idCards.back = card;
        idCards.backId = card.id;
      }
    });
    return {
      idCards,
      primaryInsuranceCards,
      secondaryInsuranceCards,
      imageCarouselObjs,
    };
  })();

  const handleCardImageClick = (imageType: string): void => {
    const index = imageCarouselObjs.findIndex((obj) => obj.alt === imageType);
    if (index > -1) {
      setZoomedIdx(index);
      setPhotoZoom(true);
    }
  };

  const handleDeleteClick = async (id: string | null): Promise<void> => {
    if (!oystehrZambda || !id) return;

    try {
      setDeletingFile(id);
      await deleteVisitFiles(oystehrZambda, { documentId: id, patientId: patientId ?? '' });
      setDeletingFile(null);
      enqueueSnackbar('File deleted successfully', { variant: 'success' });
      await refetchFileData();
    } catch (error) {
      console.error('Error deleting document:', error);
      setDeletingFile(null);
      enqueueSnackbar('Error deleting document', { variant: 'error' });
    }
  };

  // Handler for opening the scanner modal
  const handleOpenScanner = (fileType: UpdateVisitFilesInput['fileType']): void => {
    setScannerFileType(fileType);
    setScannerModalOpen(true);
  };

  // Handler for when scanning is complete
  const handleScanComplete = async (fileBlob: Blob | Blob[], fileName: string): Promise<void> => {
    if (!oystehrZambda || !appointmentID || !scannerFileType) return;

    try {
      setUploadingFileType(scannerFileType);
      // Handle PNG blobs (array of blobs)
      if (Array.isArray(fileBlob)) {
        // Upload each PNG file
        for (let i = 0; i < fileBlob.length; i++) {
          const blob = fileBlob[i];
          const pngFileName = fileBlob.length > 1 ? `${fileName}-${i + 1}.png` : `${fileName}.png`;

          const z3URL = await createZ3Object(
            {
              appointmentID,
              fileType: scannerFileType,
              fileFormat: 'png',
              file: new File([blob], pngFileName, { type: 'image/png' }),
            },
            oystehrZambda
          );

          const attachment: Attachment = {
            url: z3URL,
            title: scannerFileType,
            creation: DateTime.now().toISO(),
          };

          await filesMutation.mutateAsync({
            appointmentId: appointmentID,
            attachment,
            fileType: scannerFileType,
          });
        }

        setScannerModalOpen(false);
        setUploadingFileType(null);
        enqueueSnackbar(
          `Successfully uploaded ${fileBlob.length} scanned ${fileBlob.length === 1 ? 'image' : 'images'}`,
          { variant: 'success' }
        );
      } else {
        // Handle single PDF blob (backward compatibility)
        const pdfFileName = fileName.endsWith('.pdf') ? fileName : `${fileName}.pdf`;

        const z3URL = await createZ3Object(
          {
            appointmentID,
            fileType: scannerFileType,
            fileFormat: 'pdf',
            file: new File([fileBlob], pdfFileName, { type: 'application/pdf' }),
          },
          oystehrZambda
        );

        const attachment: Attachment = {
          url: z3URL,
          title: scannerFileType,
          creation: DateTime.now().toISO(),
        };

        await filesMutation.mutateAsync({
          appointmentId: appointmentID,
          attachment,
          fileType: scannerFileType,
        });

        setScannerModalOpen(false);
        setUploadingFileType(null);
        enqueueSnackbar('Scanned document uploaded successfully', { variant: 'success' });
      }
    } catch (error) {
      console.error('Error uploading scanned document:', error);
      setUploadingFileType(null);
      enqueueSnackbar('Error uploading scanned document', { variant: 'error' });
    }
  };

  const {
    data: visitDetailsData,
    isLoading: loading,
    refetch: refetchVisitDetails,
    isRefetching: visitDetailsAreRefreshing,
    error: visitDetailsError,
  } = useQuery({
    queryKey: ['get-visit-details', appointmentID],

    queryFn: async (): Promise<EHRVisitDetails> => {
      if (!oystehrZambda || !appointmentID) {
        throw new Error('fhir client not defined or appointmentId not provided');
      }
      return getPatientVisitDetails(oystehrZambda!, { appointmentId: appointmentID! });
    },
    enabled: Boolean(oystehrZambda) && appointmentID !== undefined,
  });

  const appointment = visitDetailsData?.appointment;
  const patient = visitDetailsData?.patient;
  const patientId = patient?.id;
  const serverConsentAttested = visitDetailsData?.consentIsAttested ?? false;

  const { data: faxData, isLoading: faxLoading } = useQuery({
    queryKey: ['get-visit-fax-history', appointmentID],

    queryFn: async (): Promise<GetVisitFaxHistoryOutput> => {
      if (oystehrZambda && appointmentID) {
        return getVisitFaxHistory(oystehrZambda, { appointmentId: appointmentID });
      }
      throw new Error('fhir client not defined or appointmentId not provided');
    },

    enabled: Boolean(oystehrZambda) && appointmentID !== undefined,
  });

  const {
    data: patientBalancesData,
    isLoading: patientBalancesLoading,
    refetch: refetchPatientBalances,
  } = useGetPatientBalances({
    patientId,
    disabled: !patientId,
  });

  useEffect(() => {
    if (consentAttested === null) {
      setConsentAttested(serverConsentAttested);
    }
  }, [serverConsentAttested, consentAttested]);

  const hasConsentChanged = consentAttested !== serverConsentAttested;

  const paperworkModifiedFlag = useMemo(
    () =>
      visitDetailsData?.flags.find(
        (resource: Flag) =>
          resource.status === 'active' && resource?.meta?.tag?.find((tag) => tag.code === 'paperwork-edit')
      ) as Flag | undefined,
    [visitDetailsData?.flags]
  );

  const encounter = visitDetailsData?.encounter;
  const qrId = visitDetailsData?.qrId;

  const {
    data: paymentData,
    refetch: refetchPaymentList,
    isRefetching: isPaymentListRefetching,
    error: paymentListError,
  } = useGetPatientPaymentsList({
    patientId: patient?.id ?? '',
    encounterId: encounter?.id ?? '',
    disabled: !encounter?.id || !patient?.id,
  });

  const refetchAllPaymentData = useCallback(async () => {
    await refetchPaymentList();
    await refetchPatientBalances();
  }, [refetchPaymentList, refetchPatientBalances]);

  const { insurance: insuranceData, isFetching } = usePatientData(patientId);

  const { isLoadingDocuments, downloadDocument } = useGetPatientDocs(patientId ?? '');

  const { fullName } = useMemo(() => {
    let fullName = '';

    if (patient) {
      fullName = getFullName(patient);
    }
    return {
      fullName,
    };
  }, [patient]);

  const isInPerson = isInPersonAppointment(appointment);

  const filesMutation = useMutation({
    mutationFn: async (input: UpdateVisitFilesInput) => {
      if (!oystehrZambda) throw new Error('oystehrZambda not defined');
      await updateVisitFiles(oystehrZambda, input);
    },
    onSuccess: async () => {
      await refetchFileData();
    },
  });

  const bookingDetailsMutation = useMutation({
    mutationFn: async (input: UpdateVisitDetailsInput) => {
      if (!oystehrZambda) throw new Error('oystehrZambda not defined');
      await updatePatientVisitDetails(oystehrZambda, input);
    },
    onSuccess: async () => {
      await refetchVisitDetails();
      setEditDialogConfig(CLOSED_EDIT_DIALOG);
      enqueueSnackbar('Patient information updated successfully', {
        variant: 'success',
      });
    },
  });

  const handleUpdateBookingDetails = async (): Promise<void> => {
    if (!appointmentID || !editDialogConfig.values || editDialogConfig.type === 'closed') {
      return;
    }
    let bookingDetails: UpdateVisitDetailsInput['bookingDetails'];
    if (editDialogConfig.type === 'dob') {
      bookingDetails = {
        confirmedDob: editDialogConfig.values.dob?.toISODate() ?? undefined,
      };
    } else if (editDialogConfig.type === 'nlg') {
      bookingDetails = {
        authorizedNonLegalGuardians: editDialogConfig.values.guardians,
      };
    } else if (editDialogConfig.type === 'service-category') {
      bookingDetails = {
        serviceCategory: editDialogConfig.values.serviceCategory,
      };
    } else {
      // type === reason-for-visit
      bookingDetails = {
        ...editDialogConfig.values,
      };
    }
    await bookingDetailsMutation.mutateAsync({
      appointmentId: appointmentID,
      bookingDetails,
    });
    if (editDialogConfig.type === 'dob') {
      await queryClient.invalidateQueries({ queryKey: ['patient-account-get'] });
    }
  };

  const dismissPaperworkFlagMutation = useMutation({
    mutationFn: async (flagId: string) => {
      if (!oystehr) {
        throw new Error('Oystehr client not found.');
      }
      return oystehr.fhir.patch({
        resourceType: 'Flag',
        id: flagId,
        operations: [
          {
            op: 'replace',
            path: '/status',
            value: 'inactive',
          },
        ],
      });
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: ['get-visit-details', appointmentID],
      });
    },
  });

  const hopInQueueMutation = useMutation({
    mutationFn: async () => {
      if (!appointment?.id || !oystehr || !user) {
        throw new Error('Missing required data');
      }

      const now = DateTime.now().toISO();
      const operation = getPatchOperationForNewMetaTag(appointment, {
        system: HOP_QUEUE_URI,
        code: now,
      });
      const updateTag = getCriticalUpdateTagOp(appointment, `Staff ${user?.email ?? `(${user?.id})`}`);

      return oystehr.fhir.patch<Appointment>({
        resourceType: 'Appointment',
        id: appointment.id,
        operations: [operation, updateTag],
      });
    },
    onSuccess: (updatedAppt) => {
      queryClient.setQueryData<EHRVisitDetails>(['get-visit-details', appointmentID], (old) =>
        old ? { ...old, appointment: updatedAppt } : old
      );
      setErrors(({ hopError: _hopError, ...rest }) => rest);
      setHopQueueDialogOpen(false);
    },
    onError: (error) => {
      console.error('Error hopping queue:', error);
      setErrors((prev) => ({
        ...prev,
        hopError: 'There was an error moving this appointment to next',
      }));
    },
  });

  useEffect(() => {
    if (visitDetailsError) {
      if (isApiError(visitDetailsError)) {
        enqueueSnackbar(visitDetailsError.message, {
          variant: 'error',
        });
      } else {
        console.error('Error fetching visit details:', visitDetailsError);
        enqueueSnackbar('An unexpected error occurred while fetching visit details.', {
          variant: 'error',
        });
      }
    }
  }, [visitDetailsError]);

  // variables for displaying the page
  const appointmentType = (appointment?.appointmentType?.text as FhirAppointmentType) || '';
  const locationTimeZone = visitDetailsData?.visitTimezone || '';
  const appointmentStartTime = DateTime.fromISO(appointment?.start ?? '').setZone(locationTimeZone);
  const appointmentTime = appointmentStartTime.toLocaleString(DateTime.TIME_SIMPLE);
  const appointmentDate = formatDateForDisplay(appointmentStartTime.toISO() || '', locationTimeZone);
  const serviceCategory = getCoding(appointment?.serviceCategory, SERVICE_CATEGORY_SYSTEM)?.code;
  const serviceCategoryLabel =
    BOOKING_CONFIG.serviceCategories.find((sc) => sc.category.code === serviceCategory)?.category.display ??
    serviceCategory ??
    '';
  const nameLastModifiedOld = formatLastModifiedTag('name', patient, locationTimeZone);
  const dobLastModifiedOld = formatLastModifiedTag('dob', patient, locationTimeZone);

  const unconfirmedDOB = appointment && getUnconfirmedDOBForAppointment(appointment);
  const getAppointmentType = (appointmentType: FhirAppointmentType | undefined): string => {
    return appointmentType === 'prebook'
      ? 'Scheduled'
      : appointmentType === 'walkin'
      ? 'On Demand'
      : appointmentType === 'posttelemed'
      ? 'Post Telemed'
      : '';
  };

  const { nameLastModified, dobLastModified } = useMemo(() => {
    let nameLastModified: string | undefined;
    let dobLastModified: string | undefined;
    if (activityLogs) {
      const nameChangelog = activityLogs.find((log) => log.activityName === ActivityName.nameChange);
      if (nameChangelog) nameLastModified = `${nameChangelog.activityDateTime} by ${nameChangelog.activityBy}`;
      const dobChangeLog = activityLogs.find((log) => log.activityName === ActivityName.dobChange);
      if (dobChangeLog) dobLastModified = `${dobChangeLog.activityDateTime} by ${dobChangeLog.activityBy}`;
    }
    return { nameLastModified, dobLastModified };
  }, [activityLogs]);

  const getAndSetHistoricResources = useCallback(
    async ({ logs, notes }: { logs?: boolean; notes?: boolean }) => {
      if (appointment) {
        const history = await getAppointmentAndPatientHistory(appointment, oystehr);
        if (history) {
          if (logs) {
            const activityLogs = formatActivityLogs({
              appointment,
              appointmentHistory: history.appointmentHistory,
              patientHistory: history.patientHistory,
              faxesSent: faxData?.faxesSent,
              paperworkStartedFlag: undefined,
              timezone: locationTimeZone,
            });
            setActivityLogs(activityLogs);
          }
          if (notes) {
            const formattedNotes = formatNotesHistory(locationTimeZone, history.appointmentHistory);
            setNotesHistory(formattedNotes);
          }
        }
        setActivityLogsLoading(false);
      }
    },
    [appointment, oystehr, locationTimeZone, faxData?.faxesSent]
  );

  useEffect(() => {
    if (!activityLogs && !faxLoading && appointment && locationTimeZone && oystehr) {
      getAndSetHistoricResources({ logs: true, notes: true }).catch((error) => {
        console.error('error getting activity logs', error);
        setActivityLogsLoading(false);
      });
    }
  }, [activityLogs, setActivityLogs, appointment, locationTimeZone, oystehr, getAndSetHistoricResources, faxLoading]);

  useEffect(() => {
    if (appointment && encounter) {
      setStatus(getInPersonVisitStatus(appointment, encounter));
    } else {
      setStatus(undefined);
    }
  }, [appointment, encounter, isInPerson]);

  // page HTML
  const handleCancelDialogOpen = (): void => {
    setCancelDialogOpen(true);
  };

  const handleCancelDialogClose = (): void => {
    setCancelDialogOpen(false);
  };

  function pdfButton(pdfUrls: string[]): ReactElement {
    const handleClick = (): void => {
      pdfUrls.forEach((url) => {
        window.open(url, '_blank');
      });
    };
    return (
      <RoundedButton
        variant="outlined"
        onClick={handleClick}
        disabled={imagesLoading}
        sx={{
          borderColor: otherColors.consentBorder,
          borderRadius: 100,
          textTransform: 'none',
          fontWeight: 500,
          fontSize: 14,
        }}
      >
        Get PDFs
      </RoundedButton>
    );
  }

  const consentEditProp = (): IconProps => {
    const ret: IconProps = {};

    if (consentPdfUrls) {
      ret[consentToTreatPatientDetailsKey] = pdfButton(consentPdfUrls);
    }

    return ret;
  };

  const signedConsentForm: {
    [consentToTreatPatientDetailsKey]?: 'Signed' | 'Not signed' | 'Loading...';
  } = loading
    ? { [consentToTreatPatientDetailsKey]: 'Loading...' }
    : (() => {
        const consentDetails = visitDetailsData?.consentDetails;
        if (consentDetails) {
          return {
            [consentToTreatPatientDetailsKey]: 'Signed',
            Signature: consentDetails.signature,
            'Full name': consentDetails.fullName,
            'Relationship to patient': consentDetails.relationshipToPatient,
            Date: consentDetails.date,
          };
        } else {
          return { [consentToTreatPatientDetailsKey]: 'Not signed' };
        }
      })();

  if (consentPdfUrls.length > 0) {
    signedConsentForm[consentToTreatPatientDetailsKey] = imagesLoading ? 'Loading...' : 'Signed';
  } else {
    signedConsentForm[consentToTreatPatientDetailsKey] = imagesLoading ? 'Loading...' : 'Not signed';
  }

  const { reasonForVisit: maybeReasonForVisit, additionalDetails } =
    getReasonForVisitAndAdditionalDetailsFromAppointment(appointment);
  const reasonForVisit = useMemo(() => {
    if (!maybeReasonForVisit) {
      return maybeReasonForVisit;
    }
    const options = getReasonForVisitOptionsForServiceCategory(serviceCategory ?? '');
    const match = options.some((option) => option.value === maybeReasonForVisit);
    return match ? maybeReasonForVisit : undefined;
  }, [maybeReasonForVisit, serviceCategory]);

  const authorizedGuardians =
    patient?.extension?.find((e) => e.url === FHIR_EXTENSION.Patient.authorizedNonLegalGuardians.url)?.valueString ??
    '';

  const downloadPaperworkPdf = async (): Promise<void> => {
    setPaperworkPdfLoading(true);
    try {
      const response = await generatePaperworkPdf(oystehrZambda!, {
        questionnaireResponseId: qrId!,
      });
      await downloadDocument(response.documentReference.split('/')[1]);
    } catch (error) {
      console.error(error);
      enqueueSnackbar('Failed to generate PDF.', { variant: 'error' });
    } finally {
      setPaperworkPdfLoading(false);
    }
  };

  const downloadVisitDetailsPdf = async (): Promise<void> => {
    if (!appointmentID) {
      enqueueSnackbar('No appointment ID found.', { variant: 'error' });
      return;
    }

    setVisitDetailsPdfLoading(true);

    try {
      const response = await getOrCreateVisitDetailsPdf(oystehrZambda!, {
        appointmentId: appointmentID,
      });
      await downloadDocument(response.documentReference.split('/')[1]);
    } catch (error) {
      console.error(error);
      enqueueSnackbar('Failed to generate PDF.', { variant: 'error' });
    } finally {
      setVisitDetailsPdfLoading(false);
    }
  };

  const patientInfoAdditionalItem: any = {};

  const patientBeenToClinicPreviously = appointment?.meta?.tag?.some(
    (tag) => tag.system === PATIENT_INFO_META_DATA_SYSTEM && tag.code === PATIENT_INFO_META_DATA_RETURNING_PATIENT_CODE
  );

  if (patientBeenToClinicPreviously) {
    patientInfoAdditionalItem['Patient has been to clinic previously'] = 'true';
  }

  const appointmentContext = useMemo(
    () => ({
      appointmentServiceCategory: serviceCategory,
      appointmentServiceMode: isTelemedAppointment(appointment) ? ServiceMode.virtual : ServiceMode['in-person'],
      reasonForVisit,
      encounterId: encounter?.id,
    }),
    [serviceCategory, appointment, reasonForVisit, encounter?.id]
  );

  return (
    <PageContainer>
      <>
        {/* Card image zoom dialog */}
        <ImageCarousel
          imagesObj={imageCarouselObjs}
          imageIndex={zoomedIdx}
          setImageIndex={setZoomedIdx}
          open={photoZoom}
          setOpen={setPhotoZoom}
        />
        {/* /* page */}
        <Grid container direction="row">
          <Grid item xs={0.25}></Grid>
          <Grid item xs={11.5}>
            <Grid container direction="row" sx={{ justifyContent: 'space-between' }}>
              <Grid item xs={6}>
                <CustomBreadcrumbs
                  chain={[
                    { link: `/patient/${patientId}`, children: 'Visit Details' },
                    { link: '#', children: appointment?.id || <Skeleton width={150} /> },
                  ]}
                />
              </Grid>
              <Grid item container xs={6} justifyContent="flex-end" gap={1}>
                <LoadingButton
                  variant="outlined"
                  sx={{
                    borderRadius: '20px',
                    textTransform: 'none',
                  }}
                  loading={visitDetailsPdfLoading}
                  color="primary"
                  disabled={isLoadingDocuments || !encounter?.id}
                  onClick={downloadVisitDetailsPdf}
                >
                  Visit Details PDF
                </LoadingButton>
                {FEATURE_FLAGS.LEGACY_DATA_ENABLED && (
                  <Button
                    variant="outlined"
                    sx={{ borderRadius: '20px', textTransform: 'none' }}
                    disabled={!patient}
                    onClick={() => {
                      const patientLastName = patient?.name?.[0]?.family ?? '';
                      const patientFirstName = patient?.name?.[0]?.given?.[0] ?? '';
                      const rawDob = patient?.birthDate ?? '';
                      // Convert YYYY-MM-DD to MM-DD-YYYY to match Z3 key format
                      const dob = rawDob ? rawDob.split('-').slice(1).concat(rawDob.split('-')[0]).join('-') : '';
                      const params = new URLSearchParams({
                        lastName: patientLastName,
                        firstName: patientFirstName,
                        dob,
                      });
                      navigate(`/legacy-data?${params.toString()}`);
                    }}
                  >
                    Legacy Data
                  </Button>
                )}
              </Grid>
            </Grid>
            {/* page title row */}
            <Grid container direction="row" marginTop={1}>
              {loading || activityLogsLoading || !patient ? (
                <Skeleton aria-busy="true" width={200} height="" />
              ) : (
                <Box
                  onClick={() => navigate(`/patient/${patientId}/info`)}
                  sx={{ cursor: 'pointer', display: 'flex', gap: 1 }}
                >
                  <AssignmentIndOutlinedIcon
                    sx={{ width: '27px', height: '27px', color: 'primary.light', alignSelf: 'center' }}
                  ></AssignmentIndOutlinedIcon>
                  <Typography
                    variant="h2"
                    color="primary.dark"
                    data-testid={dataTestIds.appointmentPage.patientFullName}
                  >
                    {fullName}
                  </Typography>
                </Box>
              )}

              {/* appointment start time as AM/PM and then date */}
              {loading || !appointment ? (
                <Skeleton sx={{ marginLeft: 2 }} aria-busy="true" width={200} />
              ) : (
                <>
                  <Typography variant="body1" sx={{ alignSelf: 'center', marginLeft: 4 }}>
                    {isInPerson ? 'In-Person' : 'Virtual'}
                    {' | '}
                    {makeAbbreviation(serviceCategoryLabel)}
                  </Typography>
                  <Typography variant="body1" sx={{ alignSelf: 'center', marginLeft: 1 }}>
                    {getAppointmentType(appointmentType)}
                  </Typography>
                  <Typography sx={{ alignSelf: 'center', marginLeft: 1 }} fontWeight="bold">
                    {appointmentTime}
                  </Typography>
                  <Typography sx={{ alignSelf: 'center', marginLeft: 2 }}>{appointmentDate}</Typography>
                </>
              )}

              {loading || !status ? (
                <Skeleton sx={{ marginLeft: 2 }} aria-busy="true" width={200} />
              ) : (
                <>
                  <Typography sx={{ alignSelf: 'center', marginLeft: 2 }}>
                    {visitDetailsData?.visitLocationName ?? ''}
                  </Typography>
                  <span
                    style={{
                      marginLeft: 20,
                      alignSelf: 'center',
                    }}
                  >
                    <InPersonAppointmentStatusChip status={status as VisitStatusLabel} />
                  </span>
                  {appointment && appointment.status === 'cancelled' && (
                    <Typography sx={{ alignSelf: 'center', marginLeft: 2 }}>
                      {getCancellationReasonDisplay(appointment)}
                    </Typography>
                  )}
                </>
              )}
              {appointment && encounter && appointment?.status !== 'cancelled' ? (
                <>
                  <Button
                    data-testid={dataTestIds.visitDetailsPage.cancelVisitButton}
                    variant="outlined"
                    sx={{
                      alignSelf: 'center',
                      marginLeft: 'auto',
                      // marginRight: 2,
                      borderRadius: '20px',
                      textTransform: 'none',
                    }}
                    color="error"
                    onClick={handleCancelDialogOpen}
                  >
                    Cancel visit
                  </Button>
                  <CancellationReasonDialog
                    handleClose={handleCancelDialogClose}
                    refetchData={async () => {
                      refetchVisitDetails().catch((error) => console.error('error refetching visit details', error));
                    }}
                    appointment={appointment}
                    encounter={encounter}
                    open={cancelDialogOpen}
                    getAndSetResources={getAndSetHistoricResources}
                  />
                </>
              ) : null}
              {status === 'arrived' ? (
                <>
                  <Button
                    variant="outlined"
                    sx={{
                      alignSelf: 'center',
                      marginLeft: 1,
                      borderRadius: '20px',
                      textTransform: 'none',
                    }}
                    disabled={!!appointment?.meta?.tag?.find((tag) => tag.system === 'hop-queue')?.code}
                    onClick={() => setHopQueueDialogOpen(true)}
                  >
                    Move to next
                  </Button>
                  <CustomDialog
                    open={hopQueueDialogOpen}
                    handleClose={() => {
                      setErrors(({ hopError: _hopError, ...rest }) => rest);
                      setHopQueueDialogOpen(false);
                    }}
                    closeButton={false}
                    title="Move to next"
                    description={`Are you sure you want to move ${patient?.name?.[0]?.family}, ${patient?.name?.[0]?.given?.[0]} to next?`}
                    closeButtonText="Cancel"
                    handleConfirm={() => hopInQueueMutation.mutate()}
                    confirmText="Move to next"
                    confirmLoading={hopInQueueMutation.isPending}
                    error={errors?.hopError}
                  />
                </>
              ) : null}
            </Grid>

            {(nameLastModifiedOld || nameLastModified) && (
              <Grid container direction="row">
                <Typography sx={{ alignSelf: 'center', marginLeft: 4, fontSize: '14px' }}>
                  Name Last Modified {nameLastModifiedOld || nameLastModified}
                </Typography>
              </Grid>
            )}

            {paperworkModifiedFlag && (
              <Grid container direction="row" marginTop={2}>
                <PaperworkFlagIndicator
                  title="Paperwork was updated:"
                  dateTime={paperworkModifiedFlag.period?.start}
                  timezone={locationTimeZone}
                  onDismiss={async () => {
                    if (paperworkModifiedFlag?.id) {
                      await dismissPaperworkFlagMutation.mutateAsync(paperworkModifiedFlag.id);
                    }
                  }}
                  color={otherColors.warningText}
                  backgroundColor={otherColors.warningBackground}
                  icon={<WarningAmberIcon sx={{ color: otherColors.warningIcon }} />}
                />
              </Grid>
            )}

            {/* new insurance card and photo id */}
            <Grid container direction="row" marginTop={2}>
              <Grid item container sx={{ padding: '10px' }}>
                <Paper sx={{ width: '100%' }}>
                  <Box padding={2}>
                    <Grid container justifyContent="space-between" sx={{ p: '0 22px' }}>
                      <Typography variant="h4" color="primary.dark">
                        Cards & IDs
                      </Typography>
                      <RoundedButton
                        to={`/patient/${patientId}/docs`}
                        startIcon={<FolderOutlinedIcon></FolderOutlinedIcon>}
                      >
                        See All Patient Docs
                      </RoundedButton>
                    </Grid>
                    <Grid container item direction="row" alignItems="center" minHeight={cardSectionHeight}>
                      <CardCategoryGridItem
                        category="primary-ins"
                        item={primaryInsuranceCards}
                        appointmentID={appointmentID}
                        filesMutator={filesMutation}
                        fullCardPdf={fullCardPdfs.find((pdf) => pdf.type === DocumentType.FullInsurance)}
                        handleImageClick={handleCardImageClick}
                        handleOpenScanner={handleOpenScanner}
                        imagesLoading={imagesLoading}
                        uploadingFileType={uploadingFileType}
                        handleDeleteClick={handleDeleteClick}
                        isDeletingFront={deletingFileId === primaryInsuranceCards.frontId}
                        isDeletingBack={deletingFileId === primaryInsuranceCards.backId}
                      />
                      <CardCategoryGridItem
                        category="secondary-ins"
                        item={secondaryInsuranceCards}
                        appointmentID={appointmentID}
                        filesMutator={filesMutation}
                        fullCardPdf={fullCardPdfs.find((pdf) => pdf.type === DocumentType.FullInsuranceSecondary)}
                        handleImageClick={handleCardImageClick}
                        handleOpenScanner={handleOpenScanner}
                        imagesLoading={imagesLoading}
                        uploadingFileType={uploadingFileType}
                        handleDeleteClick={handleDeleteClick}
                        isDeletingFront={deletingFileId === secondaryInsuranceCards.frontId}
                        isDeletingBack={deletingFileId === secondaryInsuranceCards.backId}
                      />
                      <CardCategoryGridItem
                        category="id"
                        item={idCards}
                        appointmentID={appointmentID}
                        filesMutator={filesMutation}
                        fullCardPdf={fullCardPdfs.find((pdf) => pdf.type === DocumentType.FullPhotoId)}
                        handleImageClick={handleCardImageClick}
                        handleOpenScanner={handleOpenScanner}
                        imagesLoading={imagesLoading}
                        uploadingFileType={uploadingFileType}
                        handleDeleteClick={handleDeleteClick}
                        isDeletingFront={deletingFileId === idCards.frontId}
                        isDeletingBack={deletingFileId === idCards.backId}
                      />
                    </Grid>
                  </Box>
                </Paper>
              </Grid>
            </Grid>

            <Grid container item direction="column">
              <Grid item container sx={{ padding: '10px' }} marginBottom={2}>
                <Typography variant="h3" color="primary.dark">
                  About this visit
                </Typography>
                <Grid container item direction="row" spacing={3}>
                  <Grid container item direction="column" xs={12} sm={6}>
                    <Grid item>
                      <PatientInformation
                        title="Booking details"
                        loading={loading}
                        patientDetails={{
                          ...(unconfirmedDOB
                            ? {
                                "Patient's date of birth (Unmatched)": formatDateForDisplay(unconfirmedDOB),
                              }
                            : {}),
                          'Service category': serviceCategoryLabel,
                          'Reason for visit': `${reasonForVisit} ${additionalDetails ? `- ${additionalDetails}` : ''}`,
                          'Authorized non-legal guardian(s)': patient?.extension?.find(
                            (e) => e.url === FHIR_EXTENSION.Patient.authorizedNonLegalGuardians.url
                          )?.valueString || <></>,
                          ...patientInfoAdditionalItem,
                        }}
                        icon={{
                          "Patient's date of birth (Unmatched)": (
                            <PriorityIconWithBorder fill={theme.palette.warning.main} />
                          ),
                        }}
                        editValue={{
                          "Patient's date of birth (Unmatched)": (
                            <PencilIconButton
                              onClick={() =>
                                setEditDialogConfig({
                                  type: 'dob',
                                  values: {
                                    dob:
                                      unconfirmedDOB && DateTime.fromISO(unconfirmedDOB).isValid
                                        ? DateTime.fromISO(unconfirmedDOB)
                                        : null,
                                  },
                                  keyTitleMap: {
                                    dob: 'DOB',
                                  },
                                  requiredKeys: ['dob'],
                                })
                              }
                              size="16px"
                              sx={{ mr: '5px', padding: '10px' }}
                            />
                          ),
                          'Service category': (
                            <PencilIconButton
                              onClick={() =>
                                setEditDialogConfig({
                                  type: 'service-category',
                                  values: { serviceCategory },
                                  keyTitleMap: {
                                    serviceCategory: 'Service Category',
                                  },
                                  requiredKeys: [],
                                })
                              }
                              size="16px"
                              sx={{ mr: '5px', padding: '10px' }}
                            />
                          ),
                          'Reason for visit': (
                            <PencilIconButton
                              onClick={() =>
                                setEditDialogConfig({
                                  type: 'reason-for-visit',
                                  values: { reasonForVisit, additionalDetails },
                                  keyTitleMap: {
                                    reasonForVisit: 'Reason for Visit',
                                    additionalDetails: 'Additional Details',
                                  },
                                  requiredKeys: [],
                                })
                              }
                              size="16px"
                              sx={{ mr: '5px', padding: '10px' }}
                            />
                          ),
                          'Authorized non-legal guardian(s)': (
                            <PencilIconButton
                              onClick={() =>
                                setEditDialogConfig({
                                  type: 'nlg',
                                  values: { guardians: authorizedGuardians },
                                  keyTitleMap: { guardians: 'Guardians' },
                                  requiredKeys: [],
                                })
                              }
                              size="16px"
                              sx={{ mr: '5px', padding: '10px' }}
                            />
                          ),
                        }}
                        lastModifiedBy={{
                          "Patient's date of birth (Unmatched)": dobLastModifiedOld || dobLastModified,
                        }}
                      />
                    </Grid>
                    <Grid item>
                      {/* Completed pre-visit forms */}
                      <PatientInformation
                        title="Completed consent forms"
                        loading={loading}
                        editValue={
                          signedConsentForm[consentToTreatPatientDetailsKey] === 'Signed'
                            ? consentEditProp()
                            : undefined
                        }
                        patientDetails={{
                          ...signedConsentForm,
                        }}
                        footerCellContent={
                          consentAttested !== null && (
                            <Box style={{ display: 'flex', alignItems: 'center' }}>
                              <Checkbox
                                checked={consentAttested ?? false}
                                onChange={(_e: any, checked: boolean) => {
                                  setConsentAttested(checked);
                                }}
                              />
                              <Typography>I verify that patient consent has been obtained.</Typography>
                              <LoadingButton
                                onClick={async () => {
                                  await bookingDetailsMutation
                                    .mutateAsync({
                                      appointmentId: appointment?.id ?? '',
                                      bookingDetails: {
                                        consentForms: {
                                          consentAttested: consentAttested ?? false,
                                        },
                                      },
                                    })
                                    .catch((error) => {
                                      if (isApiError(error)) {
                                        enqueueSnackbar(error.message, { variant: 'error' });
                                      } else {
                                        console.error('Error updating consent attestation:', error);
                                        enqueueSnackbar('An unexpected error occurred.', { variant: 'error' });
                                      }
                                    });
                                }}
                                loading={bookingDetailsMutation.isPending && editDialogConfig.type === 'closed'}
                                disabled={!hasConsentChanged}
                              >
                                Save
                              </LoadingButton>
                            </Box>
                          )
                        }
                      />
                    </Grid>
                  </Grid>
                  <Grid container item xs={12} sm={6} direction="column">
                    {!patientBalancesLoading &&
                    patientBalancesData?.totalBalanceCents &&
                    patientBalancesData?.totalBalanceCents > 0 ? (
                      <Grid item>
                        <PatientBalances
                          patient={patient}
                          patientBalances={patientBalancesData}
                          handleClose={refetchAllPaymentData}
                        />
                      </Grid>
                    ) : null}
                    <Grid item>
                      <PatientPaymentList
                        patient={patient}
                        appointment={appointment}
                        loading={loading || isFetching}
                        encounterId={encounter?.id ?? ''}
                        responsibleParty={{
                          fullName: visitDetailsData?.responsiblePartyName || '',
                          email: visitDetailsData?.responsiblePartyEmail || '',
                        }}
                        insuranceCoverages={insuranceData}
                        paymentData={paymentData}
                        refetchPaymentList={refetchAllPaymentData}
                        isRefetching={isPaymentListRefetching}
                        paymentListError={paymentListError}
                      />
                    </Grid>
                    <Grid item>
                      <AppointmentNotesHistory
                        appointment={appointment}
                        timezone={locationTimeZone}
                        curNoteAndHistory={notesHistory}
                        user={user}
                        oystehr={oystehr}
                        getAndSetHistoricResources={getAndSetHistoricResources}
                      />
                    </Grid>
                  </Grid>
                </Grid>
              </Grid>
            </Grid>
            <Grid item paddingY="10px" sx={{ padding: '10px' }} marginBottom={2}>
              <Typography variant="h3" color="primary.dark" marginBottom={2}>
                About this patient
              </Typography>
              <PatientAccountComponent
                id={patientId}
                loadingComponent={<Skeleton width={200} height={40} />}
                renderBackButton={false}
                appointmentContext={appointmentContext}
              />
            </Grid>
          </Grid>
        </Grid>
        {!loading && encounter && (
          <Grid container direction="row" justifyContent="space-between">
            <Grid item>
              {loading || !status ? (
                <Skeleton sx={{ marginLeft: { xs: 0, sm: 2 } }} aria-busy="true" width={200} />
              ) : (
                <div id="user-set-appointment-status">
                  <FormControl size="small" sx={{ marginTop: 2, marginLeft: { xs: 0, sm: 8 } }}>
                    <ChangeStatusDropdown
                      appointmentID={appointmentID}
                      onStatusChange={setStatus}
                      getAndSetResources={getAndSetHistoricResources}
                      dataTestId={dataTestIds.appointmentPage.changeStatusDropdown}
                    />
                  </FormControl>
                  {loading && <CircularProgress size="20px" sx={{ marginTop: 2.8, marginLeft: 1 }} />}
                </div>
              )}
            </Grid>
          </Grid>
        )}
        <Grid container direction="row">
          <Grid item sx={{ marginLeft: { xs: 0, sm: 8 }, marginTop: 2, marginBottom: 50 }}>
            <Stack direction="row" spacing={1} useFlexGap>
              <LoadingButton
                variant="outlined"
                sx={{
                  borderRadius: '20px',
                  textTransform: 'none',
                }}
                loading={paperworkPdfLoading}
                color="primary"
                disabled={isLoadingDocuments || !patientId}
                onClick={downloadPaperworkPdf}
              >
                Patient Paperwork PDF
              </LoadingButton>
              <LoadingButton
                loading={activityLogsLoading}
                variant="outlined"
                sx={{
                  borderRadius: '20px',
                  textTransform: 'none',
                }}
                size="medium"
                color="primary"
                onClick={() => setActivityLogDialogOpen(true)}
              >
                View activity logs
              </LoadingButton>
              <LoadingButton
                loading={loading}
                variant="outlined"
                sx={{
                  borderRadius: '20px',
                  textTransform: 'none',
                }}
                color="error"
                onClick={() => setIssueDialogOpen(true)}
              >
                Report Issue
              </LoadingButton>
            </Stack>
          </Grid>
        </Grid>
        {/* Update details modal */}
        <EditPatientInfoDialog
          title={dialogTitleFromType(editDialogConfig.type)}
          modalOpen={editDialogConfig.type !== 'closed'}
          onClose={() => {
            setEditDialogConfig(CLOSED_EDIT_DIALOG);
          }}
          input={
            <>
              {Object.entries(editDialogConfig.values).map(([key, value], idx) => {
                if (editDialogConfig.type === 'dob' && key === 'dob') {
                  return (
                    <DateSearch
                      key={key}
                      date={value as DateTime | null}
                      setDate={(date) =>
                        setEditDialogConfig(
                          (prev) =>
                            ({
                              ...prev,
                              values: {
                                ...prev.values,
                                [key]: date,
                              },
                            }) as EditDialogConfig
                        )
                      }
                    />
                  );
                } else if (editDialogConfig.type === 'reason-for-visit' && key === 'reasonForVisit') {
                  return (
                    <>
                      <Select
                        id="reason-for-visit-select"
                        value={value}
                        required
                        fullWidth
                        onChange={(e) =>
                          setEditDialogConfig(
                            (prev) =>
                              ({
                                ...prev,
                                values: {
                                  ...prev.values,
                                  [key]: e.target.value,
                                },
                              }) as EditDialogConfig
                          )
                        }
                      >
                        {getReasonForVisitOptionsForServiceCategory(serviceCategory ?? '').map((reason) => (
                          <MenuItem key={reason.value} value={reason.value}>
                            {reason.label}
                          </MenuItem>
                        ))}
                      </Select>
                    </>
                  );
                } else if (editDialogConfig.type === 'service-category' && key === 'serviceCategory') {
                  return (
                    <>
                      <Select
                        id="service-category-select"
                        value={value}
                        required
                        fullWidth
                        onChange={(e) =>
                          setEditDialogConfig(
                            (prev) =>
                              ({
                                ...prev,
                                values: {
                                  ...prev.values,
                                  [key]: e.target.value,
                                },
                              }) as EditDialogConfig
                          )
                        }
                      >
                        {BOOKING_CONFIG.serviceCategories.map((sc) => (
                          <MenuItem key={sc.category.code} value={sc.category.code}>
                            {sc.category.display}
                          </MenuItem>
                        ))}
                      </Select>
                    </>
                  );
                } else if (editDialogConfig.type !== 'closed') {
                  return (
                    <TextField
                      key={key}
                      label={editDialogConfig.keyTitleMap[key as keyof typeof editDialogConfig.keyTitleMap] || key}
                      fullWidth
                      value={value}
                      required={editDialogConfig.requiredKeys.includes(key)}
                      onChange={(e) =>
                        setEditDialogConfig(
                          (prev) =>
                            ({
                              ...prev,
                              values: {
                                ...prev.values,
                                [key]: e.target.value,
                              },
                            }) as EditDialogConfig
                        )
                      }
                      sx={idx > 0 ? { mt: 2 } : {}}
                    />
                  );
                } else {
                  return null;
                }
              })}
            </>
          }
          onSubmit={async (e) => {
            e.preventDefault(); // don't reload the whole page
            await handleUpdateBookingDetails();
          }}
          submitButtonName="Update"
          error={bookingDetailsMutation.isError}
          errorMessage={bookingDetailsMutation.error?.message}
          loading={bookingDetailsMutation.isPending || visitDetailsAreRefreshing}
          modalDetails={
            editDialogConfig.type === 'dob' ? (
              <Grid container spacing={2} sx={{ mt: '24px' }}>
                <Grid container item>
                  <Grid item width="35%">
                    Original DOB:
                  </Grid>
                  <Grid item>{formatDateForDisplay(patient?.birthDate)}</Grid>
                </Grid>

                {unconfirmedDOB && (
                  <Grid container item>
                    <Grid item width="35%">
                      Unmatched DOB:
                    </Grid>
                    <Grid item>{formatDateForDisplay(unconfirmedDOB)}</Grid>
                  </Grid>
                )}
              </Grid>
            ) : undefined
          }
        />
        <ActivityLogDialog
          open={activityLogDialogOpen}
          handleClose={() => setActivityLogDialogOpen(false)}
          logs={activityLogs || []}
        />
        {encounter && (
          <ReportIssueDialog
            open={issueDialogOpen}
            handleClose={() => setIssueDialogOpen(false)}
            oystehr={oystehr}
            patient={patient}
            appointment={appointment}
            encounter={encounter}
            locationId={visitDetailsData.visitLocationId}
            setSnackbarOpen={setSnackbarOpen}
            setToastType={setToastType}
            setToastMessage={setToastMessage}
          />
        )}
        <Snackbar
          open={snackbarOpen}
          autoHideDuration={6000}
          onClose={() => setSnackbarOpen(false)}
          message={toastMessage}
        >
          <Alert onClose={() => setSnackbarOpen(false)} severity={toastType} sx={{ width: '100%' }}>
            {toastMessage}
          </Alert>
        </Snackbar>
        <ScannerModal
          open={scannerModalOpen}
          onClose={() => setScannerModalOpen(false)}
          outputFormat="png"
          onScanComplete={handleScanComplete}
        />
      </>
    </PageContainer>
  );
}

interface CardCategoryGridItemInput {
  item: SavedCardItem;
  category: SavedCardCategory;
  appointmentID: string | undefined;
  filesMutator: UseMutationResult<void, Error, UpdateVisitFilesInput, unknown>;
  imagesLoading?: boolean;
  fullCardPdf?: DocumentInfo | undefined;
  uploadingFileType: UpdateVisitFilesInput['fileType'] | null;
  handleImageClick: (imageType: string) => void;
  handleOpenScanner: (fileType: UpdateVisitFilesInput['fileType']) => void;
  handleDeleteClick: (id: string | null) => Promise<void>;
  isDeletingFront: boolean;
  isDeletingBack: boolean;
}

function parseFiletype(fileUrl: string): string {
  const filetype = fileUrl.match(/\w+$/)?.[0];
  if (filetype) {
    return filetype;
  } else {
    throw new Error('Failed to parse filetype from url');
  }
}

const CardCategoryGridItem: React.FC<CardCategoryGridItemInput> = ({
  item,
  category,
  appointmentID,
  fullCardPdf,
  filesMutator,
  imagesLoading,
  uploadingFileType,
  handleImageClick,
  handleOpenScanner,
  handleDeleteClick,
  isDeletingFront,
  isDeletingBack,
}) => {
  const title = (() => {
    if (category === 'primary-ins') {
      return 'Primary Insurance Card';
    } else if (category === 'secondary-ins') {
      return 'Secondary Insurance Card';
    } else {
      return 'ID Card';
    }
  })();

  const theme = useTheme();
  const ASPECT_RATIO = 1.57; // Standard aspect ratio for ID and insurance cards
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('md'));
  const downloadDisabled = imagesLoading || (!item.front && !item.back);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState<'front' | 'back' | null>(null);

  const itemIdentifier = (side: 'front' | 'back'): UpdateVisitFilesInput['fileType'] => {
    if (category === 'primary-ins') {
      return side === 'front' ? 'insurance-card-front' : 'insurance-card-back';
    } else if (category === 'secondary-ins') {
      return side === 'front' ? 'insurance-card-front-2' : 'insurance-card-back-2';
    } else {
      return side === 'front' ? 'photo-id-front' : 'photo-id-back';
    }
  };

  const handleDownload = async (): Promise<void> => {
    try {
      for (const [key, card] of Array.from(Object.entries(item))) {
        if (card?.presignedUrl) {
          const fileType = parseFiletype(card.z3Url);

          fetch(card.presignedUrl, { method: 'GET', headers: { 'Cache-Control': 'no-cache' } })
            .then((response) => {
              if (!response.ok) {
                throw new Error('failed to fetch image from presigned url');
              }
              return response.blob();
            })
            .then((blob) => {
              const url = window.URL.createObjectURL(new Blob([blob]));
              const link = document.createElement('a');
              link.href = url;
              link.download = `${appointmentID}-${category}_${key}.${fileType}`;
              link.style.display = 'none';
              document.body.appendChild(link);
              link.click();
              document.body.removeChild(link);
            })
            .catch((error) => {
              throw new Error(error);
            });
        }
      }
    } catch (error) {
      console.error('Error downloading image:', error);
    }
  };

  return (
    <Grid container item direction="column" justifyContent="center" columnSpacing={1} xs={4} sm={4}>
      <Grid
        item
        sx={{
          paddingBottom: 1,
          '& a': {
            color: 'text.primary.light',
          },
        }}
      >
        {fullCardPdf?.presignedUrl && !downloadDisabled ? (
          <MUILink
            href={fullCardPdf.presignedUrl}
            target="_blank"
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'flex-start',
              paddingLeft: isSmallScreen ? 2 : 3,
              cursor: 'pointer',
              textDecoration: 'none',
            }}
          >
            <Typography color="primary.dark" variant="body2" textAlign="right" marginRight={1}>
              {title}
            </Typography>
            <DownloadIcon fontSize="small" color="primary" />
          </MUILink>
        ) : (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: isSmallScreen ? 'center' : 'flex-start',
              paddingLeft: isSmallScreen ? 1 : 3,
              cursor: downloadDisabled ? 'default' : 'pointer',
            }}
            onClick={async () => {
              if (downloadDisabled) return;
              await handleDownload();
            }}
          >
            <Typography color="primary.dark" variant="body2" marginRight={1}>
              {title}
            </Typography>
            <DownloadIcon fontSize="small" color="primary" sx={{ display: downloadDisabled ? 'none' : 'initial' }} />
          </Box>
        )}
      </Grid>
      <Grid item container direction="row" justifyContent={'center'} spacing={1}>
        {Object.entries(item)
          .filter(([key]) => key !== 'frontId' && key !== 'backId')
          .map(([key, card]) =>
            card ? (
              <Grid
                item
                key={card.type}
                xs={5.5}
                position={'relative'}
                sx={{
                  '&:hover .card-cancel-icon, &:focus-within .card-cancel-icon': {
                    visibility: 'visible',
                  },
                }}
              >
                <CardGridItem
                  card={card}
                  appointmentID={appointmentID}
                  fullCardPdf={fullCardPdf}
                  aspectRatio={ASPECT_RATIO}
                  handleClick={() => handleImageClick(card.type)}
                  isLoading={key === 'front' ? isDeletingFront : isDeletingBack}
                />
                <IconButton
                  className="card-cancel-icon"
                  aria-label={`Delete ${key} card`}
                  size="small"
                  sx={{
                    position: 'absolute',
                    inset: '-4px -12px auto auto',
                    color: theme.palette.error.dark,
                    backgroundColor: theme.palette.background.paper,
                    borderRadius: '50%',
                    cursor: 'pointer',
                    '&:hover, &:focus-within': { backgroundColor: theme.palette.background.paper },
                    padding: 0,
                    visibility: 'hidden',
                  }}
                  onClick={() => setDeleteDialogOpen(key as 'front' | 'back')}
                >
                  <CancelIcon />
                </IconButton>
                <CustomDialog
                  open={deleteDialogOpen === key}
                  handleClose={() => setDeleteDialogOpen(null)}
                  title="Confirm card deletion"
                  description="Are you sure you want to delete this image?"
                  closeButtonText="Cancel"
                  confirmText="Delete"
                  confirmLoading={key === 'front' ? isDeletingFront : isDeletingBack}
                  handleConfirm={async () => {
                    await handleDeleteClick(key === 'front' ? item.frontId : item.backId);
                    setDeleteDialogOpen(null);
                  }}
                />
              </Grid>
            ) : (
              <Grid item key={itemIdentifier(key as 'front' | 'back')} xs={5.5}>
                <ImageUploader
                  fileName={itemIdentifier(key as 'front' | 'back')}
                  appointmentId={appointmentID!}
                  aspectRatio={ASPECT_RATIO}
                  disabled={imagesLoading}
                  isUploading={uploadingFileType === itemIdentifier(key as 'front' | 'back')}
                  onScanClick={() => handleOpenScanner(itemIdentifier(key as 'front' | 'back'))}
                  submitAttachment={async (attachment: Attachment) => {
                    await filesMutator.mutateAsync({
                      appointmentId: appointmentID!,
                      attachment,
                      fileType: itemIdentifier(key as 'front' | 'back'),
                    });
                  }}
                />
              </Grid>
            )
          )}
      </Grid>
    </Grid>
  );
};
